import { useState, useEffect, useCallback } from 'react';
import * as previredService from '../services/previredService';

/**
 * Hook para manejar el estado de un período Previred
 * Diseñado para ser consumido por agentes autónomos vía MCP
 *
 * @param {number} idCompany - ID de la empresa
 * @param {number} mesInicial - Mes inicial
 * @param {number} anioInicial - Año inicial
 */
const usePreviredPeriodo = (idCompany, mesInicial, anioInicial) => {
  const [mes, setMes] = useState(mesInicial);
  const [anio, setAnio] = useState(anioInicial);

  // Estado del período
  const [estado, setEstado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estado de acciones
  const [descargando, setDescargando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);

  // Detalle del PDF (para modal)
  const [detalle, setDetalle] = useState(null);

  const fetchEstado = useCallback(async () => {
    if (!idCompany || !mes || !anio) return;
    setLoading(true);
    setError(null);
    try {
      const data = await previredService.getEstadoPeriodo(idCompany, mes, anio);
      setEstado(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cargar estado del período');
    } finally {
      setLoading(false);
    }
  }, [idCompany, mes, anio]);

  useEffect(() => {
    fetchEstado();
  }, [fetchEstado]);

  const descargarTxt = useCallback(async () => {
    setDescargando(true);
    try {
      await previredService.descargarTxt(idCompany, mes, anio);
      return { success: true };
    } catch (err) {
      // Manejar error de blob
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        try {
          const json = JSON.parse(text);
          throw new Error(json.message || 'Error al generar archivo');
        } catch (parseErr) {
          if (parseErr.message !== 'Error al generar archivo') throw parseErr;
          throw new Error('Error al generar archivo Previred');
        }
      }
      throw err;
    } finally {
      setDescargando(false);
    }
  }, [idCompany, mes, anio]);

  const subirPdf = useCallback(async (file, esAdicional = false) => {
    setSubiendo(true);
    try {
      const result = await previredService.subirPdf(idCompany, mes, anio, file, esAdicional);
      await fetchEstado(); // Refrescar estado
      return result;
    } finally {
      setSubiendo(false);
    }
  }, [idCompany, mes, anio, fetchEstado]);

  const verDetalle = useCallback(async (id) => {
    const data = await previredService.getDetallePdf(id);
    setDetalle(data);
    return data;
  }, []);

  const cerrarDetalle = useCallback(() => {
    setDetalle(null);
  }, []);

  const eliminarPdf = useCallback(async (id) => {
    await previredService.eliminarPdf(id);
    setDetalle(null);
    await fetchEstado();
  }, [fetchEstado]);

  const cambiarEstadoPdf = useCallback(async (id, nuevoEstado) => {
    await previredService.cambiarEstado(id, nuevoEstado);
    await fetchEstado();
  }, [fetchEstado]);

  const reprocesar = useCallback(async (id) => {
    const result = await previredService.reprocesarPdf(id);
    await fetchEstado();
    return result;
  }, [fetchEstado]);

  return {
    // Período
    mes, setMes, anio, setAnio,
    // Estado
    estado, loading, error, refrescar: fetchEstado,
    // Acciones
    descargarTxt, descargando,
    subirPdf, subiendo,
    verDetalle, cerrarDetalle, detalle,
    eliminarPdf, cambiarEstadoPdf, reprocesar,
  };
};

export default usePreviredPeriodo;
